import { setCommunityData } from "@/redux/slices";
import { RootState } from "@/redux/store";
import {
  useGetBOMPositions,
  useGetCommunities,
  useGetPermissionByCommunityId,
  useUpdatePermissionMutation,
} from "@/services";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { toast } from "@/components/Toast";
import { useQueryClient } from "@tanstack/react-query";
import { permissionActions, permissionMatrixTitles, permissionModules, permissionRoles, transformActions, transformPermissions } from "@/lib/utils";

export const PERMISSIONS = "permissions";
export const PERMISSION_MATRIX = "permissionMatrix";

const useAccessConfig = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const [communityId, setCommunityId] = useState("");
  const [communityKey, setCommunityKey] = useState(Math.random() * 2);
  const [showSaveConfirm, setShowSaveConfirm] = useState<boolean>(false);
  const [sortedBomPositions, setSortedBomPositions] = useState<any>(null);
  const [searchModuleText, setSearchModuleText] = useState<string>("");
  const [selectedModule, setSelectedModule] = useState<string>("auditTrail");
  const [lastSavedMatrix, setLastSavedMatrix] = useState<any[]>([]);

  const form = useForm<any>({
    defaultValues: {
      [PERMISSIONS]: [],
      [PERMISSION_MATRIX]: [],
    },
  });

  const communityData = useSelector(
    (state: RootState) => state.currentData.communityData
  );

  const { data: communityList, isLoading: isCommunityLoading } =
    useGetCommunities({});

  const { data: permissionList, isLoading: isPermissionLoading } =
    useGetPermissionByCommunityId({
      communityId: communityId as string,
      queryParams: {
        enabled: !!communityId,
      },
    });

  // to get all the positions for the community
  const { data: bomPositions, isLoading: isBOMPositionsLoading } =
    useGetBOMPositions({
      communityId: communityId as string,
      queryParams: {
        enabled: !!communityId,
      },
    });

  useEffect(() => {
    if (!bomPositions?.data) return;

    const sorted = [...bomPositions.data].sort((a, b) =>
      a?.bomPositionId?.toString()?.localeCompare(b?.bomPositionId?.toString())
    );

    if (JSON.stringify(sorted) !== JSON.stringify(sortedBomPositions)) {
      setSortedBomPositions(sorted);
    }
  }, [bomPositions]);

  useEffect(() => {
    if (permissionList?.data) {
      const current = form.getValues(PERMISSION_MATRIX);
      if (JSON.stringify(current) !== JSON.stringify(permissionList.data)) {
        form.setValue(PERMISSION_MATRIX, permissionList.data);
        form.setValue(PERMISSIONS, []);
        setLastSavedMatrix(JSON.parse(JSON.stringify(permissionList.data)));
      } else if (!lastSavedMatrix?.length) {
        setLastSavedMatrix(JSON.parse(JSON.stringify(permissionList.data)));
      }
    }
  }, [permissionList]);

  useEffect(() => {
    if (!communityList?.data?.length) return;

    const firstCommunity = communityList.data[0];

    if (communityData && communityId !== communityData.communityId) {
      setCommunityId(communityData.communityId);
      setCommunityKey((prev) => prev || Math.random()); // keep stable
      form.reset();
    } else if (
      !communityData &&
      firstCommunity &&
      communityId !== firstCommunity.communityId
    ) {
      setCommunityId(firstCommunity.communityId);
      dispatch(setCommunityData(firstCommunity));
      setCommunityKey((prev) => prev || Math.random()); // only once
      form.reset();
    }
  }, [communityList, dispatch, communityData, communityId]);

  const onCommunityChange = (e: any) => {
    setCommunityId(e);
    const details = communityList?.data?.find(
      (data: any) => data.communityId == e
    );
    dispatch(setCommunityData(details));
  };

  const { mutate: updatePermission, isPending: isUpdatePermissionPending } =
    useUpdatePermissionMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        toast.success("Permission Updated Successfully");
        setShowSaveConfirm(false);
        setLastSavedMatrix(
          JSON.parse(JSON.stringify(form.getValues(PERMISSION_MATRIX) || []))
        );
        form.setValue(PERMISSIONS, []);
        queryClient.invalidateQueries({
          queryKey: ["permission", communityId],
        });
        queryClient.refetchQueries({ queryKey: ["permission", communityId] }); // force refetch
      },
    });

  const onCheckboxChange = ({ obj, action, isRemoved, defaultObj }: any) => {
    /// --------------- changes in api payload object --------------------------
    const permission = [...form.getValues()[PERMISSIONS]];
    const permissionList = [...form.watch()[PERMISSION_MATRIX]];

    // Helper to manage dependent actions
    const handleDependentPermissions = (
      actions: string[],
      action: string,
      isRemoved: boolean
    ) => {
      const readRelated = [
        permissionActions.CREATE,
        permissionActions.UPDATE,
        permissionActions.APPROVE,
        permissionActions.DELETE,
        permissionActions.SUBMIT,
      ];

      // --- Add or remove READ ---
      if (readRelated.includes(action)) {
        if (isRemoved) {
          const remaining = actions.filter((a) => a !== action);
          const hasOtherReadRelated = remaining.some((a) =>
            readRelated.includes(a)
          );
          if (!hasOtherReadRelated) {
            actions = actions.filter((a) => a !== permissionActions.READ);
          }
        } else {
          if (!actions.includes(permissionActions.READ)) {
            actions.push(permissionActions.READ);
          }
        }
      }

      // --- CREATE <-> UPDATE dependency ---
      if (action === permissionActions.CREATE) {
        if (isRemoved) {
          actions = actions.filter((a) => a !== permissionActions.UPDATE);
        } else if (!actions.includes(permissionActions.UPDATE)) {
          actions.push(permissionActions.UPDATE);
        }
      } else if (action === permissionActions.UPDATE) {
        if (isRemoved) {
          actions = actions.filter((a) => a !== permissionActions.CREATE);
        } else if (!actions.includes(permissionActions.CREATE)) {
          actions.push(permissionActions.CREATE);
        }
      }

      return [...new Set(actions)]; // remove duplicates
    };

    const handlePermissionUpdate = (
      matchFn: (p: any) => boolean,
      _keyType: "BOM_POSITION" | "USER_ROLE"
    ) => {
      const existingIndex = permission.findIndex(matchFn);

      if (existingIndex !== -1) {
        // Existing record → modify actions
        let actions = [...(permission[existingIndex]?.actions || [])];
        if (isRemoved) {
          actions = actions.filter((a) => a !== action);
        } else {
          actions.push(action);
        }

        // Handle dependent permissions
        actions = handleDependentPermissions(actions, action, isRemoved);

        permission[existingIndex] = { ...permission[existingIndex], actions };

        if (permission[existingIndex]?.permissionBits >= 0)
          delete permission[existingIndex]?.permissionBits;

        if (!actions.length) {
          permission[existingIndex] = {
            ...permission[existingIndex],
            permissionBits: 0,
          };
        }
      } else {
        // New record → create entry
        const permissionArr = permissionList?.find(matchFn);
        if (permissionArr) {
          const obj = {
            ...permissionArr,
            actions: isRemoved
              ? permissionArr?.actions?.filter((p: any) => p !== action)
              : [...(permissionArr?.actions || []), action],
          };

          // Handle dependent permissions
          obj.actions = handleDependentPermissions(
            obj.actions,
            action,
            isRemoved
          );

          if (obj.permissionBits >= 0) delete obj.permissionBits;
          if (obj.isRemoved) delete obj.isRemoved;
          if (!obj.actions?.length) {
            obj.permissionBits = 0;
          }
          permission.push(obj);
        }
      }
    };

    // --- Determine which type of permission scope ---
    if (
      obj?.permissionScope === "BOM_POSITION" ||
      defaultObj?.permissionScope === "BOM_POSITION"
    ) {
      handlePermissionUpdate(
        (p: any) =>
          (obj?.permissionScope === p.permissionScope ||
            defaultObj.permissionScope === p.permissionScope) &&
          (obj?.bomPositionId === p.bomPositionId ||
            defaultObj.bomPositionId === p.bomPositionId) &&
          (obj?.module === p.module || defaultObj.module === p.module),
        "BOM_POSITION"
      );
    } else {
      handlePermissionUpdate(
        (p: any) =>
          (obj?.permissionScope === p.permissionScope ||
            defaultObj.permissionScope === p.permissionScope) &&
          (obj?.userRole === p.userRole ||
            defaultObj.userRole === p.userRole) &&
          (obj?.module === p.module || defaultObj.module === p.module),
        "USER_ROLE"
      );
    }

    permission.map((per: any) => {
      if (per?.permissionScope == "BOM_POSITION") {
        const perIndex = permissionList?.findIndex(
          (p: any) =>
            per?.permissionScope == p.permissionScope &&
            per?.bomPositionId == p.bomPositionId &&
            per?.module == p.module
        );

        permissionList[perIndex] = {
          ...permissionList[perIndex],
          actions: per.actions,
        };
      } else {
        const perIndex = permissionList?.findIndex(
          (p: any) =>
            per?.permissionScope == p.permissionScope &&
            per?.userRole == p.userRole &&
            per?.module == p.module
        );

        permissionList[perIndex] = {
          ...permissionList[perIndex],
          actions: per.actions,
        };
      }
    });
    form.setValue(PERMISSION_MATRIX, permissionList);
    form.setValue(PERMISSIONS, permission);
  };

  const buildSavePayload = (matrix: any[]) =>
    (matrix || []).map((item: any) => {
      const next = { ...item, actions: [...(item?.actions || [])] };
      if (next.permissionBits >= 0) delete next.permissionBits;
      if (next.isRemoved) delete next.isRemoved;
      if (!next.actions?.length) {
        next.permissionBits = 0;
      }
      return next;
    });

  const onSaveConfirm = () => {
    updatePermission({
      communityId: communityId as string,
      data: {
        permissions: buildSavePayload(form.getValues(PERMISSION_MATRIX) || []),
      },
    });
  };

  const onSave = () => {
    const hasChanges =
      JSON.stringify(form.getValues(PERMISSION_MATRIX)) !==
      JSON.stringify(lastSavedMatrix);
    if (hasChanges) {
      setShowSaveConfirm(true);
    } else {
      toast.warning("No permissions found for update");
    }
  };

  const onReset = () => {
    form.setValue(
      PERMISSION_MATRIX,
      JSON.parse(JSON.stringify(lastSavedMatrix || []))
    );
    form.setValue(PERMISSIONS, []);
  };

  const onChangeModuleText = (e: any) => {
    setSearchModuleText(e.target.value);
  }
  const perModules: any = useMemo(() => {
    if (searchModuleText) {
      return permissionModules?.filter((module: any) => module.label?.toLowerCase()?.includes(searchModuleText?.toLowerCase()));
    }
    return permissionModules;
  }, [searchModuleText])

  const onModuleChange = (module: any) => {
    setSelectedModule(module);
  }

  const permissionMatrix = useMemo(() => {
    const selectedModuleChildren = permissionModules?.find((module: any) => module.key?.toLowerCase() == selectedModule?.toLowerCase())?.children || [];

    return Object.entries(
      transformActions(
        transformPermissions(form.watch(PERMISSION_MATRIX))
      )
    )
    ?.filter(([moduleName]) => selectedModuleChildren?.includes(moduleName))
    ?.sort(([a], [b]) => a.toLowerCase().localeCompare(b.toLowerCase()))
  }, [selectedModule]);

  const selectedModuleName = useMemo(() => permissionModules?.find((module: any) => module.key == selectedModule)?.label, [selectedModule])

  const getRoleTypeForItem = (item: any) => {
    if (item?.permissionScope == "BOM_POSITION") return permissionRoles.BOM_POSITION;
    if (item?.userRole == "ROLE_CAM") return permissionRoles.ROLE_CAM;
    if (item?.userRole == "ROLE_CAM_ACCOUNTANT") return permissionRoles.ROLE_CAM_ACCOUNTANT;
    return item?.userRole;
  };

  const getAllowedActions = (moduleName: string, roleType: string) => {
    const titles =
      permissionMatrixTitles[moduleName as keyof typeof permissionMatrixTitles] ||
      [];
    return titles
      .filter(
        (perm: any) =>
          !Array.isArray(perm?.disabledRoles) ||
          !perm.disabledRoles.includes(roleType)
      )
      .map((perm: any) => perm.value);
  };

  const getSelectAllState = () => {
    const matrix = form.getValues(PERMISSION_MATRIX) || [];
    let total = 0;
    let checkedCount = 0;

    matrix.forEach((item: any) => {
      const allowed = getAllowedActions(item?.module, getRoleTypeForItem(item));
      total += allowed.length;
      checkedCount += allowed.filter((action) =>
        item?.actions?.includes(action)
      ).length;
    });

    if (!total || checkedCount === 0) return false;
    if (checkedCount === total) return true;
    return "indeterminate" as const;
  };

  const onSelectAll = (checked: boolean) => {
    const matrix = JSON.parse(
      JSON.stringify(form.getValues(PERMISSION_MATRIX) || [])
    );
    const updated = matrix.map((item: any) => {
      const allowed = getAllowedActions(item?.module, getRoleTypeForItem(item));
      const next = {
        ...item,
        actions: checked ? [...new Set(allowed)] : [],
      };
      if (next.permissionBits >= 0) delete next.permissionBits;
      if (next.isRemoved) delete next.isRemoved;
      if (!next.actions.length) next.permissionBits = 0;
      return next;
    });

    form.setValue(PERMISSION_MATRIX, updated);
    form.setValue(PERMISSIONS, buildSavePayload(updated));
  };

  const matrixWatch = form.watch(PERMISSION_MATRIX);
  const allSelectAllState = useMemo(
    () => getSelectAllState(),
    [matrixWatch, selectedModule, sortedBomPositions]
  );

  const totalActivities = (mdl: any) => {
    const selectedModuleChildren = permissionModules?.find((module: any) => module.key?.toLowerCase() == mdl?.toLowerCase())?.children || [];

    const matrix = Object.entries(
      transformActions(
        transformPermissions(form.watch(PERMISSION_MATRIX))
      )
    )
    ?.filter(([moduleName]) => selectedModuleChildren?.includes(moduleName))
    ?.map(([moduleName]) => {
      return permissionMatrixTitles[
        moduleName as keyof typeof permissionMatrixTitles
      ]?.length || 0;
    }).reduce((acc, val) => {
      return acc + val;
    }, 0)

    return matrix;
  };

  return {
    communityList,
    communityId,
    onCommunityChange,
    isCommunityLoading,
    communityData,
    communityKey,
    isPermissionLoading,
    bomPositions,
    isBOMPositionsLoading,
    onCheckboxChange,
    onSave,
    isUpdatePermissionPending,
    showSaveConfirm,
    setShowSaveConfirm,
    onSaveConfirm,
    sortedBomPositions,
    form,
    onChangeModuleText,
    perModules,
    searchModuleText,
    onModuleChange,
    permissionMatrix,
    selectedModule,
    selectedModuleName,
    totalActivities,
    onSelectAll,
    allSelectAllState,
    onReset,
  };
};

export default useAccessConfig;
